import type { ReactNode } from 'react';
import React, { Fragment, useState, useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import { useQuery } from '@apollo/react-hooks';

import { Inline, Grid, Box, xcss } from '@atlaskit/primitives';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import { token } from '@atlaskit/tokens';

import { ContentTreeIcon } from '@confluence/icons/entry-points/ContentTreeIcon';
import {
	confluenceLocalStorageInstance as localStorage,
	PERSISTED_KEYS_ON_SERVER as persistedLocalStorageKeys,
} from '@confluence/storage-manager';
import { ErrorDisplay } from '@confluence/error-boundary';
import type {
	ContentUnifiedQueryType,
	ContentUnifiedQueryVariablesType,
} from '@confluence/content-unified-query';
import { ContentUnifiedQuery } from '@confluence/content-unified-query';
import { useBooleanFeatureFlag } from '@confluence/session-data';

import { useCanConvertPageToFolder } from './useCanConvertPageToFolder';

const i18n = defineMessages({
	convertToFolderBanner: {
		id: 'folder.convert-page-to-folder.banner.message',
		defaultMessage:
			'Simplify your space by converting parent items to folders. <link>Show me how</link>',
		description:
			'Message on the folder conversion banner that appears on a page. It informs the user that they can convert pages to folders. At the end is a link that when clicked, opens the more actions menu which contains the "Convert to folder" button.',
	},
	bannerClose: {
		id: 'folder.convert-page-to-folder.banner.close',
		defaultMessage: 'Close',
		description:
			'Label for the x icon in the folder conversion banner. Clicking this icon closes the banner',
	},
});

const STORAGE_KEY_DISMISSED_FOLDER_CONVERSION_BANNER =
	persistedLocalStorageKeys.PERSISTED_PAGE_TO_FOLDER_CONVERSION_BANNER_DISMISSED;

const bannerStyles = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.100',
	borderBottom: `1px solid ${token('color.background.discovery.hovered')}`,
	backgroundColor: 'color.background.discovery',
});

const bannerMessageStyles = xcss({
	gridArea: 'message',
});

const closeButtonStyles = xcss({
	display: 'flex',
	justifySelf: 'end',
	gridArea: 'close',
});

type ConvertPageToFolderBannerProps = {
	contentId: string;
	spaceKey: string;
	contentType: string;
	onActionButtonClick: () => void;
};

export const ConvertPageToFolderBanner = ({
	contentId,
	spaceKey,
	contentType,
	onActionButtonClick,
}: ConvertPageToFolderBannerProps) => {
	const { formatMessage } = useIntl();
	const isSpaceAliasFFEnabled = useBooleanFeatureFlag('confluence.frontend.space.alias');
	const isNewContentTopperFFEnabled = useBooleanFeatureFlag(
		'confluence.frontend.custom-sites.page-header-and-title',
	);

	const bannerDismissedKeyExists = Boolean(
		localStorage?.getItem(
			persistedLocalStorageKeys.PERSISTED_PAGE_TO_FOLDER_CONVERSION_BANNER_DISMISSED,
		),
	);
	const [bannerDismissed, setBannerDismissed] = useState<boolean>(bannerDismissedKeyExists);

	const {
		canConvertPageToFolder,
		loading: canConvertLoading,
		error: canConvertError,
	} = useCanConvertPageToFolder({
		contentId,
		spaceKey,
		contentType,
		skipQuery: bannerDismissed,
	});

	const {
		data: contentUnifiedData,
		loading: contentUnifiedLoading,
		error: contentUnifiedError,
	} = useQuery<ContentUnifiedQueryType, ContentUnifiedQueryVariablesType>(ContentUnifiedQuery, {
		skip: bannerDismissed,
		variables: {
			contentId,
			spaceKey,
			versionOverride: null,
			isSSR: Boolean(process.env.REACT_SSR),
			includeAlias: isSpaceAliasFFEnabled,
			useNewContentTopper: isNewContentTopperFFEnabled,
		},
	});

	const showBanner = useMemo(() => {
		return (
			!bannerDismissed && canConvertPageToFolder && checkBannerConditionsForPage(contentUnifiedData)
		);
	}, [bannerDismissed, canConvertPageToFolder, contentUnifiedData]);

	const handleOnClickClose = () => {
		localStorage?.setItem(STORAGE_KEY_DISMISSED_FOLDER_CONVERSION_BANNER, true);
		setBannerDismissed(true);
	};

	const loading = canConvertLoading || contentUnifiedLoading;
	const error = canConvertError || contentUnifiedError;

	return (
		<Fragment>
			{!loading && showBanner && (
				<Grid
					templateColumns="1fr auto 1fr"
					templateAreas={['empty message close']}
					xcss={bannerStyles}
				>
					<Inline
						space="space.100"
						alignInline="center"
						alignBlock="center"
						xcss={bannerMessageStyles}
					>
						<ContentTreeIcon type="folder" label="" spacing="spacious" />
						<FormattedMessage
							{...i18n.convertToFolderBanner}
							values={{
								link: (chunks: ReactNode[]) => (
									<Button appearance="link" spacing="none" onClick={onActionButtonClick}>
										{chunks}
									</Button>
								),
							}}
						/>
					</Inline>
					<Box xcss={closeButtonStyles}>
						<IconButton
							appearance="subtle"
							spacing="compact"
							icon={EditorCloseIcon}
							label={formatMessage(i18n.bannerClose)}
							onClick={handleOnClickClose}
						/>
					</Box>
				</Grid>
			)}
			{error && <ErrorDisplay error={error} />}
		</Fragment>
	);
};

const checkBannerConditionsForPage = (contentUnifiedData: ContentUnifiedQueryType | undefined) => {
	try {
		const dynamicValue = contentUnifiedData?.content?.nodes?.[0]?.body?.dynamic?.value;
		const documentADF = dynamicValue && JSON.parse(dynamicValue);

		// Returns true if page body is empty or only contains new lines, whitespace or Children / Page Tree macros
		const isEmptyOrHasTreeMacro = documentADF?.content?.every((node: any) => {
			// Return true for Children macro
			if (node?.type === 'extension' && node?.attrs?.extensionKey === 'children') {
				return true;
			}
			// Return true for Page Tree macro or whitespace and new lines
			if (node?.type === 'paragraph') {
				// Return true for new line
				if (!node?.content) {
					return true;
				}

				// Return true for Page tree macro or whitespace
				return node?.content?.every((node: any) => {
					if (node?.type === 'text' && node?.text.trim() === '') {
						return true;
					}
					if (node?.type === 'inlineExtension' && node?.attrs?.extensionKey === 'pagetree') {
						return true;
					}
					return false;
				});
			}
			return false;
		});

		return isEmptyOrHasTreeMacro;
	} catch {
		return false;
	}
};
